/**
 * Blog-specific CTA section for article pages.
 *
 * What: The conversion band that closes every article — the shared centered
 * section head over the shared trial CTA pair, on an editorial card.
 * Why: The blog's ask is the same ask as the rest of the site, so it uses the
 * same primitives; only the labels stay blog-specific. The card, its gradient
 * rule and the reading-column width are what keep it reading as editorial.
 * When: Update when changing blog CTA strategy, layout, or conversion goals.
 */
import { Box } from '@mui/material';

import LandingCenteredSectionHead from '@/components/landing/LandingCenteredSectionHead';
import LandingSectionShell from '@/components/landing/LandingSectionShell';
import { colors, radii, shadows } from '@/components/landing/landingTokens';
import LandingTrialCtas from '@/components/landing/LandingTrialCtas';
import { useTuitionSubject } from '@/contexts/tuitionSubjectContext';

/** Section id the article page registers for scroll reveals. */
export const BLOG_ARTICLE_CTA_SECTION_ID = 'blog-article-cta';

const CTA_TITLE_ID = 'blog-article-cta-title';

interface BlogArticleCtaProps {
  /** When set, sourceLocation becomes `blog_article_${articleSlug}` for CAPI/attribution. */
  articleSlug?: string;
  /** Fallback when articleSlug is not provided (e.g. when used outside an article page). */
  sourceLocation?: string;
}

function BlogArticleCta({ articleSlug, sourceLocation }: BlogArticleCtaProps) {
  const tuition = useTuitionSubject();
  const resolvedSourceLocation = articleSlug
    ? `blog_article_${articleSlug}`
    : sourceLocation || 'blog_article_cta';

  return (
    <LandingSectionShell
      id={BLOG_ARTICLE_CTA_SECTION_ID}
      ariaLabelledby={CTA_TITLE_ID}
      // The band closes the reading column, so it owns no trailing rhythm.
      mb={0}
    >
      <Box
        sx={{
          p: { xs: 3.5, sm: 4.5, md: 5 },
          borderRadius: radii.lg,
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.paper,
          boxShadow: shadows.hero,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${colors.accentOrange} 0%, ${colors.accentCobalt} 100%)`,
          },
        }}
      >
        <LandingCenteredSectionHead
          titleId={CTA_TITLE_ID}
          eyebrow="Ready to get started?"
          title="Experience DeepThink's approach firsthand"
          body={
            tuition.subject === 'mathematics'
              ? 'Join our students who have improved their math skills with our personalized teaching methods. Start with a free trial class—no commitment required.'
              : `Join a live ${tuition.label} class with practice between lessons. Class registration for the 2027 school year is open.`
          }
        />
        <LandingTrialCtas
          sourceLocation={resolvedSourceLocation}
          align="center"
          primaryLabel={`Book a ${tuition.trial}`}
          secondaryLabel="Try the demo practice"
        />
      </Box>
    </LandingSectionShell>
  );
}

export default BlogArticleCta;
